import React, { useRef, useState } from "react";
import { Camera, Loader2, User, Lock, Save, Phone, ShieldCheck, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_AVATAR_MB = 5;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

function avatarUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND}${u}`;
}

export const AccountSettings = () => {
  const { user, updateUser } = useAuth();
  const fileRef = useRef(null);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [phone, setPhone] = useState(user?.phone || "");
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState("");
  const [phoneBusy, setPhoneBusy] = useState(false);

  const savePhone = async () => {
    setPhoneBusy(true);
    try {
      const { data } = await api.put("/auth/profile", { name: user.name, email: user.email, phone });
      updateUser(data);
      toast.success("Mobile number saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save number");
    } finally {
      setPhoneBusy(false);
    }
  };

  const sendOtp = async () => {
    setPhoneBusy(true);
    try {
      await api.post("/member/phone/send-otp", { phone });
      setOtpSent(true);
      toast.success("Verification code sent by SMS");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send code");
    } finally {
      setPhoneBusy(false);
    }
  };

  const verifyOtp = async () => {
    setPhoneBusy(true);
    try {
      const { data } = await api.post("/member/phone/verify-otp", { phone, code });
      updateUser({ ...user, phone: data.phone, phone_verified: true });
      setOtpSent(false); setCode("");
      toast.success("Mobile number verified");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
    } finally {
      setPhoneBusy(false);
    }
  };

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.put("/auth/profile", { name, email });
      updateUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Please choose a JPG, PNG, GIF or WebP image.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (file.size > MAX_AVATAR_MB * 1024 * 1024) {
      toast.error(`Image is too large — please keep it under ${MAX_AVATAR_MB}MB.`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateUser({ ...user, avatar: data.url });
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (next.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password");
    } finally {
      setSavingPw(false);
    }
  };

  const src = avatarUrl(user?.avatar);

  return (
    <div data-testid="account-settings" className="space-y-8 max-w-2xl">
      {/* Avatar + profile */}
      <div className="bg-card border border-primary/10 rounded-3xl p-7">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-9 h-9 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><User size={18} /></span>
          <h2 className="font-display text-2xl font-semibold text-primary">Profile</h2>
        </div>

        <div className="flex items-center gap-5 mb-7">
          <div className="relative">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-primary text-primary-foreground flex items-center justify-center text-xl font-semibold">
              {src ? <img data-testid="account-avatar-img" src={src} alt="" className="w-full h-full object-cover" /> : <span>{initials}</span>}
            </div>
            <button type="button" data-testid="account-avatar-upload-btn" onClick={() => fileRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center shadow-md hover:bg-primary transition-colors">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
            </button>
            <input ref={fileRef} data-testid="account-avatar-input" type="file" accept="image/*" className="hidden" onChange={onAvatarPick} />
          </div>
          <div>
            <p className="font-semibold text-primary">{user?.name}</p>
            <p className="text-sm text-muted-foreground capitalize">{user?.role} · {user?.tier?.replace("_", " ")}</p>
          </div>
        </div>

        <form onSubmit={saveProfile} className="space-y-4">
          <div>
            <Label htmlFor="acct-name">Full name</Label>
            <Input id="acct-name" data-testid="account-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="acct-email">Email</Label>
            <Input id="acct-email" data-testid="account-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
          </div>
          <Button type="submit" data-testid="account-save-profile-btn" disabled={savingProfile} className="rounded-full">
            {savingProfile ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Save changes
          </Button>
        </form>
      </div>

      {/* Mobile number */}
      <div className="bg-card border border-primary/10 rounded-3xl p-7">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-9 h-9 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><Phone size={18} /></span>
          <h2 className="font-display text-2xl font-semibold text-primary">Mobile number</h2>
          {user?.phone_verified && (
            <span data-testid="phone-verified-badge" className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              <ShieldCheck size={13} /> Verified
            </span>
          )}
        </div>
        <div className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="acct-phone">Phone (with country code)</Label>
            <Input id="acct-phone" data-testid="account-phone-input" value={phone} onChange={(e) => { setPhone(e.target.value); setOtpSent(false); }} placeholder="+1 416 555 1234" className="mt-1.5" />
            <p className="text-xs text-muted-foreground mt-1.5">Include the country code, e.g. +1 for Canada/US.</p>
          </div>
          {!otpSent ? (
            <div className="flex flex-wrap gap-3">
              <Button type="button" variant="outline" data-testid="account-phone-save-btn" onClick={savePhone} disabled={phoneBusy || !phone} className="rounded-full">
                {phoneBusy && <Loader2 size={16} className="animate-spin mr-2" />}<Save size={15} className="mr-2" />Save number
              </Button>
              <Button type="button" data-testid="account-phone-verify-btn" onClick={sendOtp} disabled={phoneBusy || !phone} className="rounded-full">
                {phoneBusy && <Loader2 size={16} className="animate-spin mr-2" />}Verify by SMS
              </Button>
            </div>
          ) : (
            <div className="rounded-2xl border border-secondary/30 bg-secondary/5 p-4 space-y-3">
              <Label htmlFor="acct-otp">Enter the 6-digit code we texted you</Label>
              <Input id="acct-otp" data-testid="account-otp-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" className="max-w-[180px]" />
              <div className="flex flex-wrap gap-3">
                <Button type="button" data-testid="account-otp-confirm-btn" onClick={verifyOtp} disabled={phoneBusy || code.length < 4} className="rounded-full">
                  {phoneBusy && <Loader2 size={16} className="animate-spin mr-2" />}<Check size={15} className="mr-2" />Confirm code
                </Button>
                <Button type="button" variant="ghost" onClick={sendOtp} disabled={phoneBusy} className="rounded-full">Resend</Button>
                <Button type="button" variant="ghost" onClick={() => { setOtpSent(false); setCode(""); }} className="rounded-full">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Password */}
      <div className="bg-card border border-primary/10 rounded-3xl p-7">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-9 h-9 rounded-full bg-secondary/15 text-secondary flex items-center justify-center"><Lock size={18} /></span>
          <h2 className="font-display text-2xl font-semibold text-primary">Change password</h2>
        </div>
        <form onSubmit={changePassword} className="space-y-4">
          <div>
            <Label htmlFor="acct-current">Current password</Label>
            <Input id="acct-current" data-testid="account-current-password" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1.5" required />
          </div>
          <div>
            <Label htmlFor="acct-new">New password</Label>
            <Input id="acct-new" data-testid="account-new-password" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1.5" required />
          </div>
          <div>
            <Label htmlFor="acct-confirm">Confirm new password</Label>
            <Input id="acct-confirm" data-testid="account-confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" required />
          </div>
          <Button type="submit" data-testid="account-change-password-btn" disabled={savingPw} className="rounded-full">
            {savingPw ? <Loader2 size={16} className="animate-spin mr-2" /> : <Lock size={16} className="mr-2" />} Update password
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AccountSettings;
